import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { applyApiFieldErrors } from '@/lib/form-errors';
import { useFacilityOptions } from '@/features/settings/users/useUsers';
import { warehouseCreateSchema, type Warehouse, type WarehouseCreateInput } from './types';
import { useCreateWarehouse, useUpdateWarehouse } from './useWarehouses';

interface WarehousesFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied the dialog runs in edit mode and PUTs `/warehouses/:id`. */
  row?: Warehouse | null;
}

export const WarehousesFormDialog = ({ open, onOpenChange, row }: WarehousesFormDialogProps) => {
  const { t } = useTranslation();
  const create = useCreateWarehouse();
  const update = useUpdateWarehouse();
  const facilitiesQuery = useFacilityOptions(open);
  const facilities = facilitiesQuery.data ?? [];
  const isEdit = !!row;

  // Creating an active warehouse needs a facility (backend refuses without
  // one), but legacy rows predate the binding — editing them for unrelated
  // fields must not demand a facility pick. superRefine keeps the inferred
  // type identical across both modes.
  const formSchema = warehouseCreateSchema
    .extend({ facility_id: z.string() })
    .superRefine((value, ctx) => {
      if (!isEdit && !value.facility_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['facility_id'],
          message: '作業拠点を選択してください',
        });
      }
    });

  const form = useForm<WarehouseCreateInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
      display_name: '',
      facility_id: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset({
        code: '',
        display_name: '',
        facility_id: '',
        notes: '',
      });
      return;
    }
    if (row) {
      form.reset({
        code: row.code,
        display_name: row.display_name,
        facility_id: row.facility_id ?? '',
        notes: row.notes ?? '',
      });
    }
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (isEdit && row) {
        await update.mutateAsync({
          id: row.warehouse_id,
          body: {
            code: values.code.trim(),
            display_name: values.display_name.trim(),
            // '' only occurs on legacy rows here; the update keeps the
            // stored value when the field is omitted.
            facility_id: values.facility_id || undefined,
            notes: values.notes?.trim() || undefined,
          },
        });
      } else {
        await create.mutateAsync({
          code: values.code.trim(),
          display_name: values.display_name.trim(),
          facility_id: values.facility_id,
          notes: values.notes?.trim() || undefined,
        });
      }
    } catch (error) {
      applyApiFieldErrors(form, error, ['code', 'display_name', 'facility_id', 'notes']);
      return;
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || update.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit
              ? t('efs.masters.warehouses.edit', { ns: 'masters' })
              : t('efs.masters.warehouses.create', { ns: 'masters' })}
          </SheetTitle>
          <SheetDescription>
            {t('efs.masters.warehouses.create_subtitle', { ns: 'masters' })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.warehouses.fields.code', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.warehouses.fields.display_name', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="facility_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.warehouses.fields.facility_id', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    {/* Operators pick the site by name — nobody knows facility
                        UUIDs, and the old free-text ID input made every create
                        fail (field report 2026-07-30). */}
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {facilities.map((f) => (
                          <SelectItem key={f.facility_id} value={f.facility_id}>
                            {f.facility_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.warehouses.fields.notes', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root?.message ? (
              <p className="text-sm font-medium text-destructive">
                {String(form.formState.errors.root.message)}
              </p>
            ) : null}

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.masters.actions.create', { ns: 'masters' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
